import React, { useState, useEffect, useMemo } from 'react';
import { OrdersPage } from './pages/OrdersPage';
import { Box, Drawer, Toolbar, List, ListItem, ListItemIcon, ListItemText, AppBar, Typography, CssBaseline, createTheme, ThemeProvider, Button, IconButton, useMediaQuery } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PeopleIcon from '@mui/icons-material/People';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import PaidIcon from '@mui/icons-material/Paid';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import BarChartIcon from '@mui/icons-material/BarChart';
import SearchIcon from '@mui/icons-material/Search';
import WidgetsIcon from '@mui/icons-material/Widgets';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ReplayIcon from '@mui/icons-material/Replay';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { canAccessGifts, GIFTS_MENU_KEYS } from './permissions/gifts';
import LoginPage from './pages/LoginPage';
import UsersPage from './pages/UsersPage';
import CustomersPage from './pages/CustomersPage';
import EmailTemplatesPage from './pages/EmailTemplatesPage';
import PushTemplatesPage from './pages/PushTemplatesPage';
import SettingsPage from './pages/SettingsPage';
import TopUpPage from './pages/TopUpPage';
import PaymentFormPage from './pages/PaymentFormPage';
import RafflesPage from './pages/RafflesPage';
import StatisticsPage from './pages/StatisticsPage';
import FindOrderPage from './pages/FindOrderPage';
import PopupMessagesPage from './pages/PopupMessagesPage';
import WinBackPage from './pages/WinBackPage';
import DeferredOrdersPage from './pages/DeferredOrdersPage';
import SubmittedOrdersPage from './pages/SubmittedOrdersPage';
import { I18nProvider, useI18n, SUPPORTED_LANGS } from './i18n';

const GiftsSettingsPage = React.lazy(() => import('./pages/gifts/GiftsSettingsPage'));
const GiftsCategoriesPage = React.lazy(() => import('./pages/gifts/GiftsCategoriesPage'));
const GiftsProductsPage = React.lazy(() => import('./pages/gifts/GiftsProductsPage'));
const GiftsDeliveryPage = React.lazy(() => import('./pages/gifts/GiftsDeliveryPage'));
const GiftsExchangeRatesPage = React.lazy(() => import('./pages/gifts/GiftsExchangeRatesPage'));
const GiftsOrdersPage = React.lazy(() => import('./pages/gifts/GiftsOrdersPage'));
const GiftsStatisticsPage = React.lazy(() => import('./pages/gifts/GiftsStatisticsPage'));
const GiftsEmailTemplatesPage = React.lazy(() => import('./pages/gifts/GiftsEmailTemplatesPage'));
const GiftsNotificationsPage = React.lazy(() => import('./pages/gifts/GiftsNotificationsPage'));
const GiftsOutboxPage = React.lazy(() => import('./pages/gifts/GiftsOutboxPage'));

const googleSansFont = document.createElement('link');
googleSansFont.href = 'https://fonts.googleapis.com/css2?family=Google+Sans+Flex:opsz,wght@6..144,400;6..144,600&display=swap';
googleSansFont.rel = 'stylesheet';
document.head.appendChild(googleSansFont);

const drawerWidth = 220;

const theme = createTheme({
  typography: {
    fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
    fontVariationSettings: '"ROND" 0, "slnt" 0, "wdth" 100',
    fontWeightLight: 100,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 600,
  },
});

function Sidebar({ onMenuClick, selected, user, selectedStatus, onStatusClick, t, lang, setLang, logout, isMobile, open, onClose }) {
  const ORDER_STATUSES = useMemo(() => ([
    { value: 'my', label: t('[Мои]'), color: '#222' },
    { value: 'new', label: t('Новый'), color: '#43a047' },
    { value: 'processing', label: t('В обработке'), color: '#1976d2' },
    { value: 'clarification', label: t('Уточнения'), color: '#fbc02d' },
    { value: 'pending', label: t('Ожидание'), color: '#757575' },
    { value: 'completed', label: t('Готово'), color: '#388e3c' },
    { value: 'cancelled', label: t('Отменено'), color: '#d32f2f' },
    { value: 'failed', label: t('Ошибка'), color: '#d32f2f' },
    { value: 'refund', label: t('Возврат'), color: '#8e24aa' },
  ]), [t]);
  const isAdmin = user?.role === 'admin';
  const canUseGifts = canAccessGifts(user?.role);
  const isStatistic = user?.role === 'statistic';
  const isController = user?.role === 'controller';
  const isOperator = user?.role === 'operator';
  const isRaffleManager = user?.role === 'raffle_manager';
  const isStoreManager = user?.role === 'store_manager';
  const canSeeRaffles = isAdmin || isRaffleManager;
  const showOrdersSection = !isRaffleManager && !isStatistic && !isStoreManager;
  const canFindOrder = !isRaffleManager && (isAdmin || isController || isOperator || isStatistic);
  const isTemplatesSelected = ['emailTemplates', 'pushTemplates'].includes(selected);
  const isSystemSelected = ['deferredOrders', 'submittedOrders'].includes(selected);
  const isGiftsSelected = GIFTS_MENU_KEYS.includes(selected);
  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? open : true}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isMobile ? '100%' : drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: isMobile ? '100%' : drawerWidth,
          maxWidth: '100%',
          boxSizing: 'border-box',
          bgcolor: '#fff',
          borderRight: '1px solid #e0e0e0',
          paddingRight: '20px'
        },
      }}
    >
      <Toolbar sx={{ pl: isMobile ? 3 : 2 }}>
        {!isMobile && (
          <Box
            component="img"
            src="/terbium_logo.svg"
            alt="Terbium"
            sx={{ width: 150, maxWidth: '100%', height: 38, objectFit: 'contain', objectPosition: 'left center' }}
          />
        )}
      </Toolbar>
      <Box sx={{ overflow: 'auto', mt: 2, flex: 1 }}>
        <List sx={{
          fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
          fontSize: '0.875rem',
          fontWeight: 600,
          '& .MuiListItem-root:hover': {
            backgroundColor: '#f3f3f3',
            '& .MuiListItemText-primary': {
              color: '#111 !important',
            },
            '& .MuiListItemIcon-root, & .MuiSvgIcon-root': {
              color: '#111',
            },
          },
        }}>
          {showOrdersSection && (
            <>
              <ListItem
                button
                selected={selected === 'orders' && !selectedStatus}
                onClick={() => { onMenuClick('orders'); onStatusClick(null); }}
                sx={{
                  cursor: 'pointer',
                  ...(selected === 'orders' && !selectedStatus
                    ? { bgcolor: '#111', color: '#fff', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }
                    : { borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }),
                  '&:hover .MuiListItemText-primary': {
                    color: '#222',
                  },
                }}
              >
                <ListItemIcon sx={{ color: selected === 'orders' && !selectedStatus ? '#fff' : '#b0b0b0', minWidth: 36 }}><AssignmentIcon /></ListItemIcon>
                <ListItemText primary={t('Заказы')}
                  primaryTypographyProps={{
                    fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: selected === 'orders' && !selectedStatus ? '#fff' : '#222',
                    m: 0
                  }}
                />
              </ListItem>
              {/* Подпункты статусов */}
              {selected === 'orders' && (
                <Box sx={{ pl: 2 }}>
                  {ORDER_STATUSES.map(status => {
                    let IconComponent = null;
                    if (status.value === 'my') {
                      IconComponent = AssignmentIcon;
                    } else {
                      switch (status.value) {
                        case 'new': IconComponent = FiberNewIcon; break;
                        case 'processing': IconComponent = AutorenewIcon; break;
                        case 'clarification': IconComponent = HelpOutlineIcon; break;
                        case 'pending': IconComponent = HourglassEmptyIcon; break;
                        case 'completed': IconComponent = DoneAllIcon; break;
                        case 'cancelled': IconComponent = CancelIcon; break;
                        case 'failed': IconComponent = ErrorOutlineIcon; break;
                        case 'refund': IconComponent = CancelIcon; break;
                        default: IconComponent = null;
                      }
                    }
                    const isSelected = selectedStatus === status.value;
                    return (
                      <ListItem
                        key={status.value}
                        button
                        selected={isSelected}
                        onClick={() => onStatusClick(status.value)}
                        sx={{
                          cursor: 'pointer',
                          minHeight: 32,
                          py: 0.5,
                          ...(isSelected
                            ? { bgcolor: '#222', color: '#fff', borderRadius: '6px !important', my: 0.25 }
                            : { borderRadius: '6px !important', my: 0.25 }),
                          '&:hover .MuiListItemText-primary': {
                            color: '#222',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {IconComponent && (
                            <IconComponent fontSize="small" sx={{ color: isSelected ? '#fff' : '#b0b0b0' }} />
                          )}
                        </ListItemIcon>
                        <ListItemText primary={status.label}
                          primaryTypographyProps={{
                            fontSize: '0.85rem',
                            fontWeight: 500,
                            color: isSelected ? '#fff' : '#555',
                            fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                            ml: 2
                          }}
                        />
                      </ListItem>
                    );
                  })}
                </Box>
              )}
            </>
          )}
          {canFindOrder && (
            <ListItem
              button
              selected={selected === 'findOrder'}
              onClick={() => { onMenuClick('findOrder'); onStatusClick(null); }}
              sx={{
                cursor: 'pointer',
                ...(selected === 'findOrder'
                  ? { bgcolor: '#111', color: '#fff', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }
                  : { borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }),
                '&:hover .MuiListItemText-primary': {
                  color: '#222',
                },
              }}
            >
              <ListItemIcon sx={{ color: selected === 'findOrder' ? '#fff' : '#b0b0b0', minWidth: 36 }}><SearchIcon /></ListItemIcon>
              <ListItemText primary={t('Найти заказ')}
                primaryTypographyProps={{
                  fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: selected === 'findOrder' ? '#fff' : '#222',
                  m: 0
                }}
              />
            </ListItem>
          )}
          {/* Остальные пункты меню только для админа */}
          {isAdmin && (
            <>
              <ListItem
                button
                selected={selected === 'popupMessages'}
                onClick={() => { onMenuClick('popupMessages'); onStatusClick(null); }}
                sx={{
                  cursor: 'pointer',
                  ...(selected === 'popupMessages'
                    ? { bgcolor: '#111', color: '#fff', borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }
                    : { borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }),
                  '&:hover .MuiListItemText-primary': { color: '#222' },
                }}
              >
                <ListItemIcon sx={{ color: selected === 'popupMessages' ? '#fff' : '#b0b0b0', minWidth: 36 }}>
                  <WidgetsIcon />
                </ListItemIcon>
                <ListItemText
                  primary={t('Виджеты')}
                  primaryTypographyProps={{
                    fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: selected === 'popupMessages' ? '#fff' : '#222',
                  }}
                />
              </ListItem>
              {selected === 'popupMessages' && (
                <Box sx={{ pl: 2 }}>
                  <ListItem
                    button
                    selected
                    onClick={() => { onMenuClick('popupMessages'); onStatusClick(null); }}
                    sx={{
                      cursor: 'pointer',
                      minHeight: 32,
                      py: 0.5,
                      bgcolor: '#222',
                      color: '#fff',
                      borderRadius: '6px !important',
                      my: 0.25,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <MessageOutlinedIcon fontSize="small" sx={{ color: '#fff' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('Сообщения')}
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        color: '#fff',
                        fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                        ml: 2,
                      }}
                    />
                  </ListItem>
                </Box>
              )}
            </>
          )}
          {isAdmin && (
            <>
              <ListItem
                button
                selected={selected === 'winBack'}
                onClick={() => { onMenuClick('winBack'); onStatusClick(null); }}
                sx={{
                  cursor: 'pointer',
                  ...(selected === 'winBack'
                    ? { bgcolor: '#111', color: '#fff', borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }
                    : { borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }),
                  '&:hover .MuiListItemText-primary': { color: '#222' },
                }}
              >
                <ListItemIcon sx={{ color: selected === 'winBack' ? '#fff' : '#b0b0b0', minWidth: 36 }}>
                  <LocalOfferIcon />
                </ListItemIcon>
                <ListItemText
                  primary={t('Акции')}
                  primaryTypographyProps={{
                    fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: selected === 'winBack' ? '#fff' : '#222',
                  }}
                />
              </ListItem>
              {selected === 'winBack' && (
                <Box sx={{ pl: 2 }}>
                  <ListItem
                    button
                    selected
                    onClick={() => { onMenuClick('winBack'); onStatusClick(null); }}
                    sx={{
                      cursor: 'pointer',
                      minHeight: 32,
                      py: 0.5,
                      bgcolor: '#222',
                      color: '#fff',
                      borderRadius: '6px !important',
                      my: 0.25,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <ReplayIcon fontSize="small" sx={{ color: '#fff' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('Win-back')}
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        color: '#fff',
                        fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                        ml: 2,
                      }}
                    />
                  </ListItem>
                </Box>
              )}
            </>
          )}
          {isAdmin && (
            <ListItem
              button
              selected={selected === 'users'}
              onClick={() => { onMenuClick('users'); onStatusClick(null); }}
              sx={{
                cursor: 'pointer',
                ...(selected === 'users'
                  ? { bgcolor: '#111', color: '#fff', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }
                  : { borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }),
                '&:hover .MuiListItemText-primary': {
                  color: '#222',
                },
              }}
            >
              <ListItemIcon sx={{ color: selected === 'users' ? '#fff' : '#b0b0b0', minWidth: 36 }}><PeopleIcon /></ListItemIcon>
              <ListItemText primary={t('Сотрудники')}
                primaryTypographyProps={{
                  fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: selected === 'users' ? '#fff' : '#222',
                  m: 0
                }}
              />
            </ListItem>
          )}
          {isAdmin && (
            <ListItem
              button
              selected={selected === 'customers'}
              onClick={() => { onMenuClick('customers'); onStatusClick(null); }}
              sx={{
                cursor: 'pointer',
                ...(selected === 'customers'
                  ? { bgcolor: '#111', color: '#fff', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }
                  : { borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }),
                '&:hover .MuiListItemText-primary': {
                  color: '#222',
                },
              }}
            >
              <ListItemIcon sx={{ color: selected === 'customers' ? '#fff' : '#b0b0b0', minWidth: 36 }}><PeopleIcon /></ListItemIcon>
              <ListItemText primary={t('Клиенты')}
                primaryTypographyProps={{
                  fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: selected === 'customers' ? '#fff' : '#222',
                  m: 0
                }}
              />
            </ListItem>
          )}
          {isAdmin && (
            <>
              <ListItem
                button
                selected={isTemplatesSelected}
                onClick={() => { onMenuClick('emailTemplates'); onStatusClick(null); }}
                sx={{
                  cursor: 'pointer',
                  ...(isTemplatesSelected
                    ? { bgcolor: '#111', color: '#fff', borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }
                    : { borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }),
                  '&:hover .MuiListItemText-primary': { color: '#222' },
                }}
              >
                <ListItemIcon sx={{ color: isTemplatesSelected ? '#fff' : '#b0b0b0', minWidth: 36 }}>
                  <DescriptionOutlinedIcon />
                </ListItemIcon>
                <ListItemText
                  primary={t('Шаблоны')}
                  primaryTypographyProps={{
                    fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: isTemplatesSelected ? '#fff' : '#222',
                  }}
                />
              </ListItem>
              {isTemplatesSelected && (
                <Box sx={{ pl: 2 }}>
                  <ListItem
                    button
                    selected={selected === 'emailTemplates'}
                    onClick={() => { onMenuClick('emailTemplates'); onStatusClick(null); }}
                    sx={{
                      cursor: 'pointer',
                      minHeight: 32,
                      py: 0.5,
                      bgcolor: selected === 'emailTemplates' ? '#222' : 'transparent',
                      color: selected === 'emailTemplates' ? '#fff' : '#555',
                      borderRadius: '6px !important',
                      my: 0.25,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <EmailOutlinedIcon fontSize="small" sx={{ color: selected === 'emailTemplates' ? '#fff' : '#b0b0b0' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('Email шаблоны')}
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        color: selected === 'emailTemplates' ? '#fff' : '#555',
                        fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                        ml: 2,
                      }}
                    />
                  </ListItem>
                  <ListItem
                    button
                    selected={selected === 'pushTemplates'}
                    onClick={() => { onMenuClick('pushTemplates'); onStatusClick(null); }}
                    sx={{
                      cursor: 'pointer',
                      minHeight: 32,
                      py: 0.5,
                      bgcolor: selected === 'pushTemplates' ? '#222' : 'transparent',
                      color: selected === 'pushTemplates' ? '#fff' : '#555',
                      borderRadius: '6px !important',
                      my: 0.25,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <NotificationsActiveOutlinedIcon fontSize="small" sx={{ color: selected === 'pushTemplates' ? '#fff' : '#b0b0b0' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('Push шаблоны')}
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        color: selected === 'pushTemplates' ? '#fff' : '#555',
                        fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                        ml: 2,
                      }}
                    />
                  </ListItem>
                </Box>
              )}
            </>
          )}
          {(isAdmin || isStatistic) && (
            <ListItem
              button
              selected={selected === 'statistics'}
              onClick={() => { onMenuClick('statistics'); onStatusClick(null); }}
              sx={{
                cursor: 'pointer',
                ...(selected === 'statistics'
                  ? { bgcolor: '#111', color: '#fff', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }
                  : { borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }),
                '&:hover .MuiListItemText-primary': {
                  color: '#222',
                },
              }}
            >
              <ListItemIcon sx={{ color: selected === 'statistics' ? '#fff' : '#b0b0b0', minWidth: 36 }}><BarChartIcon /></ListItemIcon>
              <ListItemText primary={t('Статистика')}
                primaryTypographyProps={{
                  fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: selected === 'statistics' ? '#fff' : '#222',
                  m: 0
                }}
              />
            </ListItem>
          )}
          {canUseGifts && (
            <>
            <ListItem disablePadding sx={{ my: 0.5 }}>
              <Button
                onClick={() => { onMenuClick('giftsSettings'); onStatusClick(null); }}
                aria-expanded={isGiftsSelected}
                startIcon={<StorefrontOutlinedIcon />}
                sx={{
                  width: '100%', justifyContent: 'flex-start', px: 2, py: 1,
                  textTransform: 'none', fontSize: '0.875rem', fontWeight: 600,
                  borderRadius: '0 6px 6px 0',
                  bgcolor: isGiftsSelected ? '#111' : 'transparent',
                  color: isGiftsSelected ? '#fff' : '#222',
                  '& .MuiButton-startIcon': { mr: 2, color: isGiftsSelected ? '#fff' : '#b0b0b0' },
                  '&:hover': { bgcolor: '#f3f3f3', color: '#111' },
                }}
              >{t('Магазин')}</Button>
            </ListItem>
            {isGiftsSelected && <Box sx={{ pl: 2 }}>
              {[
                { key: 'giftsSettings', label: 'Управление магазином', icon: <SettingsIcon fontSize="small" /> },
                { key: 'giftsCategories', label: 'Категории', icon: <CategoryOutlinedIcon fontSize="small" /> },
                { key: 'giftsProducts', label: 'Товары', icon: <Inventory2OutlinedIcon fontSize="small" /> },
                { key: 'giftsDelivery', label: 'Доставка', icon: <LocalShippingOutlinedIcon fontSize="small" /> },
                { key: 'giftsRates', label: 'Курсы валют', icon: <CurrencyExchangeIcon fontSize="small" /> },
                { key: 'giftsOrders', label: 'Заказы магазина', icon: <AssignmentIcon fontSize="small" /> },
                { key: 'giftsStatistics', label: 'Статистика магазина', icon: <BarChartIcon fontSize="small" /> },
                { key: 'giftsEmailTemplates', label: 'Email-шаблоны магазина', icon: <EmailOutlinedIcon fontSize="small" /> },
                { key: 'giftsNotifications', label: 'Уведомления сотрудников', icon: <NotificationsActiveOutlinedIcon fontSize="small" /> },
                { key: 'giftsOutbox', label: 'Очередь писем', icon: <PendingActionsIcon fontSize="small" /> },
              ].map(item => <ListItem key={item.key} disablePadding sx={{ my: 0.25 }}>
                <Button onClick={() => { onMenuClick(item.key); onStatusClick(null); }} aria-current={selected === item.key ? 'page' : undefined} startIcon={item.icon}
                  sx={{ width: '100%', justifyContent: 'flex-start', textTransform: 'none', textAlign: 'left', fontSize: '0.82rem', lineHeight: 1.4, py: 1, px: 1.5, color: selected === item.key ? '#fff' : '#555', bgcolor: selected === item.key ? '#222' : 'transparent', '&:hover': { bgcolor: '#f3f3f3', color: '#111' } }}>
                  {t(item.label)}
                </Button>
              </ListItem>)}
            </Box>}
            </>
          )}
          {canSeeRaffles && (
            <ListItem
              button
              selected={selected === 'raffles'}
              onClick={() => { onMenuClick('raffles'); onStatusClick(null); }}
              sx={{
                cursor: 'pointer',
                ...(selected === 'raffles'
                  ? { bgcolor: '#111', color: '#fff', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }
                  : { borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }),
                '&:hover .MuiListItemText-primary': {
                  color: '#222',
                },
              }}
            >
              <ListItemIcon sx={{ color: selected === 'raffles' ? '#fff' : '#b0b0b0', minWidth: 36 }}><ConfirmationNumberIcon /></ListItemIcon>
              <ListItemText primary={t('Розыгрыш')}
                primaryTypographyProps={{
                  fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: selected === 'raffles' ? '#fff' : '#222',
                  m: 0
                }}
              />
            </ListItem>
          )}
          {isAdmin && (
            <ListItem
              button
              selected={selected === 'paymentForm'}
              onClick={() => { onMenuClick('paymentForm'); onStatusClick(null); }}
              sx={{
                cursor: 'pointer',
                ...(selected === 'paymentForm'
                  ? { bgcolor: '#111', color: '#fff', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }
                  : { borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }),
                '&:hover .MuiListItemText-primary': {
                  color: '#222',
                },
              }}
            >
              <ListItemIcon sx={{ color: selected === 'paymentForm' ? '#fff' : '#b0b0b0', minWidth: 36 }}><PaidIcon /></ListItemIcon>
              <ListItemText primary={t('Форма оплаты')}
                primaryTypographyProps={{
                  fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: selected === 'paymentForm' ? '#fff' : '#222',
                  m: 0
                }}
              />
            </ListItem>
          )}
          {isAdmin && (
            <>
              <ListItem
                button
                selected={isSystemSelected}
                onClick={() => { onMenuClick('deferredOrders'); onStatusClick(null); }}
                sx={{
                  cursor: 'pointer',
                  ...(isSystemSelected
                    ? { bgcolor: '#111', color: '#fff', borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }
                    : { borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }),
                  '&:hover .MuiListItemText-primary': { color: '#222' },
                }}
              >
                <ListItemIcon sx={{ color: isSystemSelected ? '#fff' : '#b0b0b0', minWidth: 36 }}>
                  <SettingsApplicationsIcon />
                </ListItemIcon>
                <ListItemText
                  primary={t('Система')}
                  primaryTypographyProps={{
                    fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: isSystemSelected ? '#fff' : '#222',
                  }}
                />
              </ListItem>
              {isSystemSelected && (
                <Box sx={{ pl: 2 }}>
                  <ListItem
                    button
                    selected={selected === 'deferredOrders'}
                    onClick={() => { onMenuClick('deferredOrders'); onStatusClick(null); }}
                    sx={{
                      cursor: 'pointer',
                      minHeight: 32,
                      py: 0.5,
                      bgcolor: selected === 'deferredOrders' ? '#222' : 'transparent',
                      color: selected === 'deferredOrders' ? '#fff' : '#555',
                      borderRadius: '6px !important',
                      my: 0.25,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <PendingActionsIcon fontSize="small" sx={{ color: selected === 'deferredOrders' ? '#fff' : '#b0b0b0' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('Отложенные')}
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        color: selected === 'deferredOrders' ? '#fff' : '#555',
                        fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                        ml: 2,
                      }}
                    />
                  </ListItem>
                  <ListItem
                    button
                    selected={selected === 'submittedOrders'}
                    onClick={() => { onMenuClick('submittedOrders'); onStatusClick(null); }}
                    sx={{
                      cursor: 'pointer',
                      minHeight: 32,
                      py: 0.5,
                      bgcolor: selected === 'submittedOrders' ? '#222' : 'transparent',
                      color: selected === 'submittedOrders' ? '#fff' : '#555',
                      borderRadius: '6px !important',
                      my: 0.25,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CloudSyncIcon fontSize="small" sx={{ color: selected === 'submittedOrders' ? '#fff' : '#b0b0b0' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('В работе')}
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        color: selected === 'submittedOrders' ? '#fff' : '#555',
                        fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                        ml: 2,
                      }}
                    />
                  </ListItem>
                </Box>
              )}
            </>
          )}
          {isAdmin && (
            <ListItem
              button
              selected={selected === 'settings'}
              onClick={() => { onMenuClick('settings'); onStatusClick(null); }}
              sx={{
                cursor: 'pointer',
                ...(selected === 'settings'
                  ? { bgcolor: '#111', color: '#fff', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }
                  : { borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderTopRightRadius: 6, borderBottomRightRadius: 6, my: 0.5 }),
                '&:hover .MuiListItemText-primary': {
                  color: '#222',
                },
              }}
            >
              <ListItemIcon sx={{ color: selected === 'settings' ? '#fff' : '#b0b0b0', minWidth: 36 }}><SettingsIcon /></ListItemIcon>
              <ListItemText primary={t('Настройки')}
                primaryTypographyProps={{
                  fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  color: selected === 'settings' ? '#fff' : '#222',
                  m: 0
                }}
              />
            </ListItem>
          )}
        </List>
      </Box>
      {isMobile && (
        <Box sx={{ borderTop: '1px solid #eee', p: 2, pr: 0 }}>
          <Typography sx={{ mb: 1.5, fontSize: '0.9rem', fontWeight: 600, color: '#555' }}>
            {user.role}: {user.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            {SUPPORTED_LANGS.map((item) => (
              <Button
                key={item.code}
                size="small"
                variant={lang === item.code ? 'contained' : 'outlined'}
                onClick={() => setLang(item.code)}
                sx={{ flex: 1, fontWeight: 700 }}
              >
                {item.code.toUpperCase()}
              </Button>
            ))}
          </Box>
          {!isRaffleManager && !isStatistic && (
            <Button
              fullWidth
              variant="contained"
              startIcon={<PaidIcon />}
              onClick={() => onMenuClick('topup')}
              sx={{ mb: 1, fontWeight: 600 }}
            >
              {t('Пополнить')}
            </Button>
          )}
          <Button fullWidth color="inherit" onClick={logout} startIcon={<ExitToAppIcon />} sx={{ fontWeight: 600 }}>
            {t('Выход')}
          </Button>
        </Box>
      )}
    </Drawer>
  );
}

function AppContent() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const isMobile = useMediaQuery('(max-width:899.95px)');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menu, setMenu] = useState(() => {
    if (user?.role === 'raffle_manager') return 'raffles';
    if (user?.role === 'statistic') return 'statistics';
    if (user?.role === 'store_manager') return 'giftsSettings';
    return 'orders';
  });
  const [selectedStatus, setSelectedStatus] = useState(null);
  const isController = user?.role === 'controller';
  const isRaffleManager = user?.role === 'raffle_manager';
  const isStoreManager = user?.role === 'store_manager';
  const isAdmin = user?.role === 'admin';
  const canUseGifts = canAccessGifts(user?.role);
  const isStatistic = user?.role === 'statistic';
  const isOperator = user?.role === 'operator';
  const canSeeRaffles = isAdmin || isRaffleManager;
  const canFindOrder = !isRaffleManager && (isAdmin || isController || isOperator || isStatistic);
  const [menuRestored, setMenuRestored] = useState(false);

  const sanitizeMenu = React.useCallback((value) => {
    const allowed = [
      !isRaffleManager && !isStatistic && !isStoreManager ? 'orders' : null,
      canFindOrder ? 'findOrder' : null,
      isAdmin ? 'users' : null,
      isAdmin ? 'customers' : null,
      isAdmin ? 'emailTemplates' : null,
      isAdmin ? 'pushTemplates' : null,
      isAdmin ? 'popupMessages' : null,
      isAdmin ? 'winBack' : null,
      isAdmin ? 'deferredOrders' : null,
      isAdmin ? 'submittedOrders' : null,
      isAdmin || isStatistic ? 'statistics' : null,
      canSeeRaffles ? 'raffles' : null,
      isAdmin ? 'settings' : null,
      isAdmin ? 'paymentForm' : null,
      ...(canUseGifts ? GIFTS_MENU_KEYS : []),
      !isRaffleManager && !isStatistic && !isStoreManager ? 'topup' : null,
    ].filter(Boolean);
    return allowed.includes(value) ? value : null;
  }, [canFindOrder, canSeeRaffles, canUseGifts, isAdmin, isRaffleManager, isStatistic, isStoreManager]);

  React.useEffect(() => {
    if (!user || menuRestored) return;
    const savedMenu = localStorage.getItem('admin_menu');
    const restored = savedMenu ? sanitizeMenu(savedMenu) : null;
    if (restored) {
      setMenu(restored);
    }
    setMenuRestored(true);
  }, [menuRestored, sanitizeMenu, user]);

  React.useEffect(() => {
    if (!user || !menuRestored) return;
    localStorage.setItem('admin_menu', menu);
  }, [menu, menuRestored, user]);

  // Ограничения меню по роли применяются также при смене пользователя.
  React.useEffect(() => {
    if (!user || !menuRestored) return;
    if (GIFTS_MENU_KEYS.includes(menu) && !canUseGifts) {
      setMenu(isStatistic ? 'statistics' : (isRaffleManager ? 'raffles' : 'orders'));
      setSelectedStatus(null);
      return;
    }
    if (isStoreManager && !GIFTS_MENU_KEYS.includes(menu)) {
      setMenu('giftsSettings');
      setSelectedStatus(null);
      return;
    }
    if (isStatistic && !['statistics', 'findOrder'].includes(menu)) {
      setMenu('statistics');
      setSelectedStatus(null);
      return;
    }
    if (isController && !['orders', 'findOrder'].includes(menu)) {
      setMenu('orders');
      setSelectedStatus(null);
      return;
    }
    if (isRaffleManager && menu !== 'raffles') {
      setMenu('raffles');
      setSelectedStatus(null);
    }
  }, [user, canUseGifts, isController, isRaffleManager, isStatistic, isStoreManager, menu, menuRestored]);

// --- Начало блока для WebSocket logout ---
useEffect(() => {
  if (!user) return;
  if(!user.device_id) logout();
  // Генерация или получение deviceId
  // let deviceId = localStorage.getItem('deviceId');
  // if (!deviceId) {
  //   deviceId = (window.crypto?.randomUUID?.() ?? Math.random().toString(36).slice(2));
  //   localStorage.setItem('deviceId', deviceId);
  // }

}, [user, logout]);

// Синхронизация логаута между вкладками
useEffect(() => {
  function onStorage(e) {
    if (e.key === 'was_logged_out' && e.newValue === '1') {
      console.log("Storage changed logout");
      logout();
    }
  }
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}, [logout]);
// --- Конец блока для WebSocket logout ---

  const handleMenuClick = React.useCallback((value) => {
    setMenu(value);
    if (isMobile) setMobileMenuOpen(false);
  }, [isMobile]);

  const handleStatusClick = React.useCallback((value) => {
    setSelectedStatus(value);
    if (isMobile) setMobileMenuOpen(false);
  }, [isMobile]);

  if (!user) {
    return <LoginPage />;
  }

  const menuTitle = (() => {
    switch (menu) {
      case 'orders': return t('Заказы');
      case 'users': return t('Сотрудники');
      case 'customers': return t('Клиенты');
      case 'emailTemplates': return t('Email шаблоны');
      case 'pushTemplates': return t('Push шаблоны');
      case 'popupMessages': return t('Сообщения');
      case 'winBack': return t('Win-back');
      case 'deferredOrders': return t('Отложенные');
      case 'submittedOrders': return t('В работе');
      case 'statistics': return t('Статистика');
      case 'raffles': return t('Розыгрыш');
      case 'paymentForm': return t('Форма оплаты');
      case 'settings': return t('Настройки');
      case 'giftsSettings': return t('Магазин');
      case 'giftsCategories': return t('Категории магазина');
      case 'giftsProducts': return t('Товары магазина');
      case 'giftsDelivery': return t('Доставка подарков');
      case 'giftsRates': return t('Курсы валют магазина');
      case 'giftsOrders': return t('Заказы магазина');
      case 'giftsStatistics': return t('Статистика магазина');
      case 'giftsEmailTemplates': return t('Email-шаблоны магазина');
      case 'giftsNotifications': return t('Уведомления сотрудников');
      case 'giftsOutbox': return t('Очередь писем магазина');
      case 'topup': return t('Пополнение');
      case 'findOrder': return t('Найти заказ');
      default: return t('Выход');
    }
  })();

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', minHeight: '100vh', overflowX: 'hidden' }}>
        <CssBaseline />
        <Sidebar
          onMenuClick={handleMenuClick}
          selected={menu}
          user={user}
          selectedStatus={selectedStatus}
          onStatusClick={handleStatusClick}
          t={t}
          lang={lang}
          setLang={setLang}
          logout={logout}
          isMobile={isMobile}
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        />
        <Box component="main" sx={{ flexGrow: 1, bgcolor: '#f7f8fa', minHeight: '100vh', p: { xs: 2, md: 4 }, width: 0, display: 'flex', flexDirection: 'column' }}>
          <AppBar
            position="fixed"
            sx={{
              zIndex: 1201,
              ml: { xs: 0, md: `${drawerWidth}px` },
              width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
              bgcolor: '#fff',
              color: '#222',
              boxShadow: 0,
              borderBottom: '1px solid #eee',
            }}
          >
            <Toolbar sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                color="inherit"
                edge="start"
                onClick={() => setMobileMenuOpen((open) => !open)}
                aria-label={t(mobileMenuOpen ? 'Закрыть меню' : 'Открыть меню')}
                sx={{ display: { xs: 'inline-flex', md: 'none' } }}
              >
                {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{
                  color: '#1976d2',
                  fontWeight: 700,
                  minWidth: { xs: 0, md: 160 },
                  flexGrow: { xs: 1, md: 0 },
                  maxWidth: '40vw',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {menuTitle}
              </Typography>
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2, flexShrink: 0, ml: 'auto' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f5f5f5', borderRadius: 999, px: 1 }}>
                  {SUPPORTED_LANGS.map((item) => (
                    <Button
                      key={item.code}
                      size="small"
                      onClick={() => setLang(item.code)}
                      sx={{
                        minWidth: 70,
                        width: 70,
                        color: lang === item.code ? '#1976d2' : '#555',
                        fontWeight: 700,
                        textTransform: 'none',
                        bgcolor: lang === item.code ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                        '&:hover': { bgcolor: 'rgba(25, 118, 210, 0.12)' },
                        borderRadius: 999,
                        px: 1,
                        gap: 0.4,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <img
                          src={item.icon}
                          alt={item.label}
                          style={{ width: 18, height: 14, borderRadius: 2, objectFit: 'cover' }}
                          onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.parentElement.textContent = item.flag || item.code.toUpperCase(); }}
                        />
                        <span style={{ fontSize: 14, fontWeight: 700, minWidth: 20, textAlign: 'center', display: 'inline-block' }}>
                          {item.code.toUpperCase()}
                        </span>
                      </span>
                    </Button>
                  ))}
                </Box>
                <Box sx={{
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  color: '#1976d2',
                  letterSpacing: 0.2,
                  minWidth: 120,
                  justifyContent: 'center',
                }}>
                  {user.role}: {user.name}
                </Box>
                {!isRaffleManager && !isStatistic && !isStoreManager && (
                  <Button variant="contained" color="primary" onClick={() => setMenu('topup')} sx={{ fontWeight: 600 }}>
                    {t('Пополнить')}
                  </Button>
                )}
                <Button color="inherit" onClick={logout} startIcon={<ExitToAppIcon />} sx={{ fontWeight: 600 }}>
                  {t('Выход')}
                </Button>
              </Box>
            </Toolbar>
          </AppBar>
          <Toolbar />
          <Box sx={{ mt: 2, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {menu === 'orders' && !isRaffleManager && !isStatistic && !isStoreManager && <OrdersPage operator={user.name} searchTerm={selectedStatus === 'my' ? '__MY__' : (selectedStatus || '')} />}
            {menu === 'findOrder' && canFindOrder && <FindOrderPage />}
            {isAdmin && menu === 'users' && <UsersPage />}
            {isAdmin && menu === 'customers' && <CustomersPage />}
            {isAdmin && menu === 'emailTemplates' && <EmailTemplatesPage />}
            {isAdmin && menu === 'pushTemplates' && <PushTemplatesPage />}
            {isAdmin && menu === 'popupMessages' && <PopupMessagesPage />}
            {isAdmin && menu === 'winBack' && <WinBackPage />}
            {isAdmin && menu === 'deferredOrders' && <DeferredOrdersPage />}
            {isAdmin && menu === 'submittedOrders' && <SubmittedOrdersPage />}
            {(isAdmin || isStatistic) && menu === 'statistics' && <StatisticsPage />}
            {canSeeRaffles && menu === 'raffles' && <RafflesPage />}
            {isAdmin && menu === 'settings' && <SettingsPage />}
            {canUseGifts && menu === 'giftsSettings' && <React.Suspense fallback={<Typography>{t('Загрузка')}</Typography>}><GiftsSettingsPage /></React.Suspense>}
            {canUseGifts && menu === 'giftsCategories' && <React.Suspense fallback={<Typography>{t('Загрузка')}</Typography>}><GiftsCategoriesPage /></React.Suspense>}
            {canUseGifts && menu === 'giftsProducts' && <React.Suspense fallback={<Typography>{t('Загрузка')}</Typography>}><GiftsProductsPage /></React.Suspense>}
            {canUseGifts && menu === 'giftsDelivery' && <React.Suspense fallback={<Typography>{t('Загрузка')}</Typography>}><GiftsDeliveryPage /></React.Suspense>}
            {canUseGifts && menu === 'giftsOrders' && <React.Suspense fallback={<Typography>{t('Загрузка')}</Typography>}><GiftsOrdersPage /></React.Suspense>}
            {canUseGifts && menu === 'giftsStatistics' && <React.Suspense fallback={<Typography>{t('Загрузка')}</Typography>}><GiftsStatisticsPage /></React.Suspense>}
            {canUseGifts && menu === 'giftsEmailTemplates' && <React.Suspense fallback={<Typography>{t('Загрузка')}</Typography>}><GiftsEmailTemplatesPage /></React.Suspense>}
            {canUseGifts && menu === 'giftsNotifications' && <React.Suspense fallback={<Typography>{t('Загрузка')}</Typography>}><GiftsNotificationsPage /></React.Suspense>}
            {canUseGifts && menu === 'giftsOutbox' && <React.Suspense fallback={<Typography>{t('Загрузка')}</Typography>}><GiftsOutboxPage /></React.Suspense>}
            {canUseGifts && menu === 'giftsRates' && <React.Suspense fallback={<Typography>{t('Загрузка')}</Typography>}><GiftsExchangeRatesPage /></React.Suspense>}
            {isAdmin && menu === 'paymentForm' && <PaymentFormPage />}
            {menu === 'topup' && !isRaffleManager && !isStatistic && !isStoreManager && <TopUpPage />}
            {!isController && menu === 'logout' && <Typography variant="h5">{t('Выход')}</Typography>}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
